import {useEffect, useState} from 'react';
import {useLocation, NavLink} from 'react-router-dom';
import PropTypes from 'prop-types';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Icon from '@mui/material/Icon';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import SidenavCollapse from 'examples/Sidenav/SidenavCollapse';

import SidenavRoot from 'examples/Sidenav/SidenavRoot';
import sidenavLogoLabel from 'examples/Sidenav/styles/sidenav';

import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from 'context';

function Sidenav({color, brand, brandName, routes, ...rest}) {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    sidenavColor,
  } = controller;
  const location = useLocation();
  // const collapseName = location.pathname.replace('/', '');
  const collapseName = location.pathname.split('/', 2)[1];

  let textColor = 'white';

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = 'dark';
  } else if (whiteSidenav && darkMode) {
    textColor = 'inherit';
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(
        dispatch,
        window.innerWidth < 1200 ? false : transparentSidenav,
      );
      setWhiteSidenav(
        dispatch,
        window.innerWidth < 1200 ? false : whiteSidenav,
      );
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener('resize', handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleMiniSidenav);
  }, [dispatch, location]);

  const [openGroups, setOpenGroups] = useState({});
  const toggleGroup = (key) => setOpenGroups(prev => ({...prev, [key]: !prev[key]}));

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(
    ({type, name, icon, title, noCollapse, key, href, route, children}) => {
      let returnValue;

      if (type === 'group') {
        const isOpen = openGroups[key] ?? children?.some(c => c.key === collapseName);
        returnValue = (
          <div key={key}>
            {/* Group header */}
            <MDBox
              onClick={() => toggleGroup(key)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1,
                mx: 1,
                borderRadius: '8px',
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': { background: 'rgba(255,255,255,0.1)' },
              }}
            >
              <MDBox
                sx={{
                  width: 32, height: 32, borderRadius: '8px', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', mr: 1.5,
                  background: 'rgba(255,255,255,0.15)',
                  flexShrink: 0,
                }}
              >
                <Icon sx={{ color: '#fff', fontSize: '1rem !important' }}>{icon}</Icon>
              </MDBox>
              <MDTypography
                variant="button"
                fontWeight="medium"
                color={textColor}
                sx={{ flex: 1, fontSize: '0.8rem', opacity: miniSidenav ? 0 : 1, transition: 'opacity 0.2s' }}
              >
                {name}
              </MDTypography>
              {!miniSidenav && (
                <Icon sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '1rem !important', transition: 'transform 0.2s', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                  chevron_right
                </Icon>
              )}
            </MDBox>
            {/* Children */}
            {isOpen && !miniSidenav && (
              <MDBox sx={{ pl: 2, borderLeft: '2px solid rgba(255,255,255,0.15)', ml: 3.5, mt: 0.5, mb: 0.5 }}>
                {children?.map(child => (
                  <NavLink key={child.key} to={child.route} style={{ textDecoration: 'none' }}>
                    <MDBox
                      sx={{
                        display: 'flex', alignItems: 'center', px: 1.5, py: 0.75,
                        borderRadius: '8px', mx: 0.5, mb: 0.25,
                        background: child.key === collapseName ? 'rgba(255,255,255,0.2)' : 'transparent',
                        '&:hover': { background: 'rgba(255,255,255,0.12)' },
                        cursor: 'pointer',
                      }}
                    >
                      <Icon sx={{ color: child.key === collapseName ? '#fff' : 'rgba(255,255,255,0.6)', fontSize: '0.9rem !important', mr: 1 }}>
                        {child.icon}
                      </Icon>
                      <MDTypography
                        variant="caption"
                        fontWeight={child.key === collapseName ? 'bold' : 'regular'}
                        sx={{ color: child.key === collapseName ? '#fff' : 'rgba(255,255,255,0.75)', fontSize: '0.78rem' }}
                      >
                        {child.name}
                      </MDTypography>
                    </MDBox>
                  </NavLink>
                ))}
              </MDBox>
            )}
          </div>
        );
      } else if (type === 'collapse') {
        returnValue = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{textDecoration: 'none'}}>
            <SidenavCollapse
              name={name}
              icon={icon}
              active={key === collapseName}
              noCollapse={noCollapse}
            />
          </Link>
        ) : (
          <NavLink key={key} to={route}>
            <SidenavCollapse
              name={name}
              icon={icon}
              active={key === collapseName}
            />
          </NavLink>
        );
      } else if (type === 'title') {
        returnValue = (
          <MDTypography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}>
            {title}
          </MDTypography>
        );
      } else if (type === 'divider') {
        returnValue = (
          <Divider
            key={key}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      }

      if (key === 'sign-in') {
        return null; // don't show sign-in link in sidenav
      }

      return returnValue;
    },
  );

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{transparentSidenav, whiteSidenav, miniSidenav, darkMode}}>
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        <MDBox
          display={{xs: 'block', xl: 'none'}}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{cursor: 'pointer'}}>
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{fontWeight: 'bold'}}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox component={NavLink} to="/" display="flex" alignItems="center">
          {brand && (
            <MDBox component="img" src={brand} alt="Brand" width="2rem" />
          )}
          <MDBox
            width={!brandName && '100%'}
            sx={theme => sidenavLogoLabel(theme, {miniSidenav})}>
            <MDTypography
              component="h6"
              variant="button"
              fontWeight="medium"
              color={textColor}>
              {brandName}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List>{renderRoutes}</List>
      <MDBox p={2} mt="auto">
        <MDButton
          component="a"
          href="https://www.creative-tim.com/product/material-dashboard-pro-react"
          target="_blank"
          rel="noreferrer"
          variant="gradient"
          color={sidenavColor}
          fullWidth>
          upgrade to pro
        </MDButton>
      </MDBox>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: 'info',
  brand: '',
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf([
    'primary',
    'secondary',
    'info',
    'success',
    'warning',
    'error',
    'dark',
  ]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
